'use client';

import { useMemo, useState } from 'react';
import AppFooter from '@/components/AppFooter';
import AppLogo from '@/components/AppLogo';
import BottomNavigation from '@/components/BottomNavigation';
import CalendarMonthView from '@/components/CalendarMonthView';
import { CategoryIcon } from '@/components/CategoryIcon';
import { useAppSettings } from '@/hooks/useAppSettings';
import { useCategories } from '@/hooks/useCategories';
import { useRecurringExpenses } from '@/hooks/useRecurringExpenses';
import { useTransactions } from '@/hooks/useTransactions';
import { todayDateKey } from '@/lib/date';
import { formatMoney } from '@/lib/money';
import { recurringDateForMonth } from '@/lib/recurring';

export default function CalendarPage() {
  const { transactions, loading, error } = useTransactions();
  const { categories, error: categoriesError } = useCategories();
  const { settings, error: settingsError } = useAppSettings();
  const { recurringExpenses, loading: loadingRecurring, error: recurringError } = useRecurringExpenses();
  const [selectedDate, setSelectedDate] = useState<string>(todayDateKey());
  const pageError = error || categoriesError || settingsError || recurringError;
  const money = (value: number) => formatMoney(value, settings.currency);

  const selectedDayTransactions = useMemo(
    () => transactions.filter((tx) => tx.date === selectedDate),
    [transactions, selectedDate]
  );

  const selectedDayRecurringExpenses = useMemo(() => {
    const [year, month] = selectedDate.split('-').map(Number);
    return recurringExpenses.filter(
      (item) => item.active && recurringDateForMonth(item, year, month - 1) === selectedDate
    );
  }, [recurringExpenses, selectedDate]);

  const selectedDayIncome = selectedDayTransactions
    .filter((tx) => tx.type === 'income')
    .reduce((sum, tx) => sum + tx.amount, 0);
  const selectedDayExpense =
    selectedDayTransactions.filter((tx) => tx.type === 'expense').reduce((sum, tx) => sum + tx.amount, 0) +
    selectedDayRecurringExpenses.reduce((sum, item) => sum + item.amount, 0);
  const selectedDayBalance = selectedDayIncome - selectedDayExpense;
  const selectedDayCount = selectedDayTransactions.length + selectedDayRecurringExpenses.length;
  const selectedDateLabel = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(selectedDate));
  const selectedDayBalanceTone = selectedDayBalance >= 0 ? 'text-emerald-200' : 'text-rose-200';

  return (
    <main className="page-shell">
      <div className="page-inner">
        {pageError ? (
          <div className="rounded-2xl border border-rose-900/70 bg-rose-950 p-4 text-sm text-rose-200">
            {pageError}
          </div>
        ) : null}

        <section className="overflow-hidden rounded-2xl border border-brand-500/20 bg-slate-900 shadow-sm">
          <div className="bg-brand-700 px-4 py-5 text-white min-[380px]:px-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="min-w-0">
                <AppLogo className="text-brand-100" markClassName="border-white/25" textClassName="text-brand-100" />
                <h1 className="mt-1 text-xl font-semibold leading-tight text-white min-[380px]:text-2xl">
                  Mois en un coup d'oeil
                </h1>
              </div>
              <div className="rounded-2xl bg-slate-950/45 p-3 sm:w-44">
                <p className="text-xs font-medium capitalize text-brand-100">{selectedDateLabel}</p>
                <p className={`mt-1 text-lg font-semibold ${selectedDayBalanceTone}`}>
                  {loading || loadingRecurring ? '...' : money(selectedDayBalance)}
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 min-[380px]:p-4 sm:p-5">
            <div className="mb-4 grid grid-cols-3 gap-2">
              <div className="rounded-2xl bg-slate-950 p-3">
                <p className="text-xs font-medium text-slate-400">Entrées</p>
                <p className="mt-1 truncate text-sm font-semibold text-emerald-300 min-[380px]:text-base">
                  {loading || loadingRecurring ? '...' : money(selectedDayIncome)}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-950 p-3">
                <p className="text-xs font-medium text-slate-400">Sorties</p>
                <p className="mt-1 truncate text-sm font-semibold text-rose-300 min-[380px]:text-base">
                  {loading || loadingRecurring ? '...' : money(selectedDayExpense)}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-950 p-3">
                <p className="text-xs font-medium text-slate-400">Solde</p>
                <p className={`mt-1 truncate text-sm font-semibold min-[380px]:text-base ${selectedDayBalanceTone}`}>
                  {loading || loadingRecurring ? '...' : money(selectedDayBalance)}
                </p>
              </div>
            </div>

            <CalendarMonthView
              transactions={transactions}
              categories={categories}
              recurringExpenses={recurringExpenses}
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              currency={settings.currency}
            />
          </div>
        </section>

        <section className="section-card">
          <div className="mb-4 flex flex-col gap-2 min-[420px]:flex-row min-[420px]:items-end min-[420px]:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium capitalize text-slate-400">{selectedDateLabel}</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-100">Transactions du jour</h2>
            </div>
            <span className="w-fit rounded-full bg-slate-800 px-3 py-1 text-xs font-semibold text-slate-300">
              {selectedDayCount}
            </span>
          </div>

          <div className="space-y-3">
            {loading || loadingRecurring ? (
              <div className="card-muted">Chargement...</div>
            ) : selectedDayTransactions.length === 0 && selectedDayRecurringExpenses.length === 0 ? (
              <div className="card-muted text-sm">Aucune transaction ce jour.</div>
            ) : (
              <>
                {selectedDayRecurringExpenses.map((item) => {
                  const category = categories.find((categoryItem) => categoryItem.id === item.categoryId);
                  return (
                    <div key={item.id} className="rounded-2xl border border-brand-900/70 bg-brand-950/40 p-4 shadow-sm">
                      <div className="flex flex-col gap-3 min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between">
                        <div className="flex min-w-0 items-center gap-2">
                          {category ? <CategoryIcon category={category} className="h-8 w-8" /> : null}
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-slate-100">{item.name}</p>
                            <p className="mt-1 text-xs font-medium text-brand-200">Achat récurrent</p>
                          </div>
                        </div>
                        <p className="text-base font-semibold text-rose-400 min-[380px]:text-sm">-{money(item.amount)}</p>
                      </div>
                    </div>
                  );
                })}

                {selectedDayTransactions.map((tx) => {
                  const isIncome = tx.type === 'income';
                  const category = categories.find((item) => item.id === tx.categoryId);
                  return (
                    <div key={tx.id} className="rounded-2xl border border-slate-800 bg-slate-900 p-4 shadow-sm">
                      <div className="flex flex-col gap-3 min-[380px]:flex-row min-[380px]:items-center min-[380px]:justify-between">
                        <div className="flex min-w-0 items-center gap-2">
                          {category ? <CategoryIcon category={category} className="h-8 w-8" /> : null}
                          <p className="truncate font-semibold text-slate-100">
                            {category?.name ?? (isIncome ? 'Entrée' : 'Dépense')}
                          </p>
                        </div>
                        <p className={`text-base font-semibold min-[380px]:text-sm ${isIncome ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {isIncome ? '+' : '-'}{money(tx.amount)}
                        </p>
                      </div>
                      {tx.note ? <p className="mt-2 text-sm text-slate-400">{tx.note}</p> : null}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </section>
      </div>
      <BottomNavigation active="/calendar" />
      <div className="page-inner mt-5">
        <AppFooter />
      </div>
    </main>
  );
}
